import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import {
  getCourses, createCourse, updateCourse, publishCourse, archiveCourse, deleteCourse,
  getBatches, createBatch, openBatch, startBatch, completeBatch
} from '../../services/lmsService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import SimpleRichTextEditor from '../ui/rich-text-editor';
import { toast } from 'sonner';
import Footer from '../Footer';
import {
  Plus, Pencil, Trash2, Eye, Archive, Send, BookOpen,
  Users, Calendar, ChevronRight, GraduationCap, Clock,
  FileText, MoreVertical, Settings, Award, Layers, RefreshCw, Video, Tag, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Utility function to strip HTML tags from text
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const LMSAdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
  
  // Modals
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  // Certificate templates
  const CERTIFICATE_TEMPLATES = [
    { id: 'classic', name: 'Classic', gradient: 'from-indigo-500 via-purple-500 to-pink-500', accent: 'indigo' },
    { id: 'elegant', name: 'Elegant Gold', gradient: 'from-amber-500 via-yellow-500 to-orange-500', accent: 'amber' },
    { id: 'modern', name: 'Modern Blue', gradient: 'from-blue-500 via-cyan-500 to-teal-500', accent: 'blue' },
    { id: 'professional', name: 'Professional', gradient: 'from-gray-700 via-gray-600 to-gray-800', accent: 'gray' },
    { id: 'vibrant', name: 'Vibrant', gradient: 'from-rose-500 via-pink-500 to-purple-500', accent: 'rose' },
    { id: 'nature', name: 'Nature Green', gradient: 'from-emerald-500 via-green-500 to-teal-500', accent: 'emerald' },
  ];
  
  // Forms
  const [courseForm, setCourseForm] = useState({
    title: '', short_description: '', full_description: '', duration_weeks: 8,
    mode: 'live_web', difficulty_level: 'intermediate', language: 'English',
    is_free: false, price_inr: 0, certificate_enabled: true, recordings_enabled: true,
    certificate_template: 'classic',
    certificate_custom_text: '',
    certificate_signatory_name: '',
    certificate_signatory_title: '',
    instructor_name: '',
    instructor_bio: '',
    instructor_image: '',
    thumbnail_url: '',
    categories: []  // NEW
  });
  
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const thumbnailInputRef = React.useRef(null);
  
  const [batchForm, setBatchForm] = useState({
    course_id: '', name: '', description: '', start_date: '', end_date: '',
    max_students: 50,
    schedule_pattern: { type: 'weekly', weekly_days: [6], weekly_frequency: 'every_week', class_time: '19:00', class_duration_minutes: 60 }
  });
  
  useEffect(() => {
    // Allow both admin and sub_admin roles
    if (!user || (user.lms_role !== 'admin' && user.lms_role !== 'sub_admin')) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [user, activeTab]);
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  // Fetch data when page changes
  useEffect(() => {
    if (user && (user.lms_role === 'admin' || user.lms_role === 'sub_admin')) {
      fetchData();
    }
  }, [pagination.page]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'courses') {
        const response = await getCourses({ page: pagination.page, limit: 20 });
        if (response.success) {
          setCourses(response.data.courses);
          setPagination(response.data.pagination);
        }
      } else if (activeTab === 'batches') {
        const response = await getBatches({ page: pagination.page, limit: 20 });
        if (response.success) {
          setBatches(response.data.batches);
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCourse = async () => {
    try {
      const response = await createCourse(courseForm);
      if (response.success) {
        toast.success('Course created successfully');
        setShowCourseModal(false);
        resetCourseForm();
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    }
  };
  
  const handleUpdateCourse = async () => {
    try {
      const response = await updateCourse(editingCourse.id, courseForm);
      if (response.success) {
        toast.success('Course updated');
        setShowCourseModal(false);
        setEditingCourse(null);
        resetCourseForm();
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update course');
    }
  };
  
  const handlePublishCourse = async (courseId) => {
    try {
      const response = await publishCourse(courseId);
      if (response.success) { toast.success('Course published'); fetchData(); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to publish'); }
  };
  
  const handleArchiveCourse = async (courseId) => {
    try {
      const response = await archiveCourse(courseId);
      if (response.success) { toast.success('Course archived'); fetchData(); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to archive'); }
  };
  
  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      const response = await deleteCourse(courseId);
      if (response.success) { toast.success('Course deleted'); fetchData(); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };
  
  const handleCreateBatch = async () => {
    try {
      const response = await createBatch(batchForm);
      if (response.success) {
        toast.success(`Batch created with ${response.data.sessions_generated} sessions`);
        setShowBatchModal(false);
        resetBatchForm();
        fetchData();
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create batch'); }
  };
  
  const handleOpenBatch = async (batchId) => {
    try {
      const response = await openBatch(batchId);
      if (response.success) { toast.success('Batch opened'); fetchData(); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const handleStartBatch = async (batchId) => {
    try {
      const response = await startBatch(batchId);
      if (response.success) { toast.success('Batch started'); fetchData(); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const handleCompleteBatch = async (batchId) => {
    try {
      const response = await completeBatch(batchId);
      if (response.success) { toast.success(`Batch completed. ${response.data.certificates_issued} certificates issued.`); fetchData(); }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };
  
  const resetCourseForm = () => {
    setCourseForm({ title: '', short_description: '', full_description: '', duration_weeks: 8, mode: 'live_web', difficulty_level: 'intermediate', language: 'English', is_free: false, price_inr: 0, certificate_enabled: true, recordings_enabled: true, thumbnail_url: '', categories: [], certificate_template: 'classic', certificate_signatory_name: '', certificate_signatory_title: '', certificate_custom_text: '', instructor_name: '', instructor_bio: '', instructor_image: '' });
    setThumbnailFile(null);
  };
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/lms/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };
  
  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);
  
  // Create new category
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/lms/categories?name=${encodeURIComponent(newCategoryName)}&description=&color=%233B82F6`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success('Category created successfully!');
        setNewCategoryName('');
        setShowNewCategoryInput(false);
        fetchCategories();
      } else {
        toast.error(data.detail || 'Failed to create category');
      }
    } catch (error) {
      toast.error('Failed to create category');
    }
  };
  
  // Toggle category selection
  const toggleCategorySelection = (categoryName) => {
    setCourseForm(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryName)
        ? prev.categories.filter(c => c !== categoryName)
        : [...prev.categories, categoryName]
    }));
  };
  
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'video/mp4', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, WebP) or video (MP4, WebM)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadingThumbnail(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setCourseForm({ ...courseForm, thumbnail_url: base64String });
        setThumbnailFile(file);
        toast.success('Thumbnail uploaded successfully');
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Thumbnail upload error:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };
  
  const removeThumbnail = () => {
    setCourseForm({ ...courseForm, thumbnail_url: '' });
    setThumbnailFile(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };
  
  const resetBatchForm = () => {
    setBatchForm({ course_id: '', name: '', description: '', start_date: '', end_date: '', max_students: 50, schedule_pattern: { type: 'weekly', weekly_days: [6], weekly_frequency: 'every_week', class_time: '19:00', class_duration_minutes: 60 } });
  };
  
  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({ title: course.title, short_description: course.short_description, full_description: course.full_description || '', duration_weeks: course.duration_weeks, mode: course.mode, difficulty_level: course.difficulty_level, language: course.language, is_free: course.is_free, price_inr: course.price_inr, certificate_enabled: course.certificate_enabled, recordings_enabled: course.recordings_enabled, thumbnail_url: course.thumbnail_url || '', categories: course.categories || [], certificate_template: course.certificate_template || 'classic', certificate_signatory_name: course.certificate_signatory_name || '', certificate_signatory_title: course.certificate_signatory_title || '', certificate_custom_text: course.certificate_custom_text || '', instructor_name: course.instructor_name || '', instructor_bio: course.instructor_bio || '', instructor_image: course.instructor_image || '' });
    setShowCourseModal(true);
  };
  
  const getStatusBadge = (status) => {
    const badges = { draft: <Badge variant="secondary">Draft</Badge>, published: <Badge className="bg-green-100 text-green-700">Published</Badge>, archived: <Badge variant="outline">Archived</Badge>, open: <Badge className="bg-blue-100 text-blue-700">Open</Badge>, in_progress: <Badge className="bg-indigo-100 text-indigo-700">In Progress</Badge>, completed: <Badge className="bg-green-100 text-green-700">Completed</Badge> };
    return badges[status] || <Badge>{status}</Badge>;
  };

  if (!user || (user.lms_role !== 'admin' && user.lms_role !== 'sub_admin')) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <Settings className="h-7 w-7 sm:h-8 sm:w-8 text-indigo-600" />
                  LMS Admin Dashboard
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">Manage courses, batches, and enrollments</p>
              </div>
              <Button onClick={() => navigate('/calendar')} variant="outline" className="w-full sm:w-auto">
                <Calendar className="w-4 h-4 mr-2" />Calendar
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardContent className="p-3 sm:p-4">
                  <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600 mb-2" />
                  <p className="text-xl sm:text-2xl font-bold">{pagination.total_items || courses.length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Courses</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50">
                <CardContent className="p-3 sm:p-4">
                  <Layers className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mb-2" />
                  <p className="text-xl sm:text-2xl font-bold">{batches.length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Batches</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-3 sm:p-4">
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mb-2" />
                  <p className="text-xl sm:text-2xl font-bold">{courses.reduce((sum, c) => sum + (c.total_enrolled || 0), 0)}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Students</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50">
                <CardContent className="p-3 sm:p-4">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600 mb-2" />
                  <p className="text-xl sm:text-2xl font-bold">{courses.filter(c => c.status === 'published').length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Published</p>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(newTab) => {
            setActiveTab(newTab);
            setPagination({ page: 1, total_pages: 1, total_items: 0 });
          }}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
              <TabsList className="w-full sm:w-auto">
                <TabsTrigger value="courses" className="flex-1 sm:flex-none"><BookOpen className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Courses</span></TabsTrigger>
                <TabsTrigger value="batches" className="flex-1 sm:flex-none"><Layers className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">Batches</span></TabsTrigger>
              </TabsList>
              
              <div className="flex gap-2 w-full sm:w-auto">
                {activeTab === 'courses' && (
                  <Button className="bg-indigo-600 hover:bg-indigo-700 flex-1 sm:flex-none" onClick={() => { setEditingCourse(null); resetCourseForm(); setShowCourseModal(true); }}>
                    <Plus className="w-4 h-4 mr-2" />New Course
                  </Button>
                )}
                {activeTab === 'batches' && (
                  <Button className="bg-indigo-600 hover:bg-indigo-700 flex-1 sm:flex-none" onClick={() => { resetBatchForm(); setShowBatchModal(true); }} disabled={courses.length === 0}>
                    <Plus className="w-4 h-4 mr-2" />New Batch
                  </Button>
                )}
              </div>
            </div>
            
            {/* Courses Tab */}
            <TabsContent value="courses">
              {courses.length === 0 ? (
                <Card className="text-center py-8 sm:py-12">
                  <CardContent>
                    <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No courses yet</h3>
                    <p className="text-gray-500 text-sm mb-4">Create your first course to get started</p>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowCourseModal(true)}><Plus className="w-4 h-4 mr-2" />Create Course</Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {courses.map((course) => (
                    <Card key={course.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{course.title}</h3>
                              {getStatusBadge(course.status)}
                              <Badge variant="outline" className="capitalize text-xs">{course.mode === 'live_web' ? 'Live Web' : course.mode === 'self_paced' ? 'Self-Paced' : course.mode}</Badge>
                            </div>
                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{stripHtmlTags(course.short_description)}</p>
                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3 sm:w-4 sm:h-4" />{course.duration_weeks}w</span>
                              <span className="flex items-center gap-1"><Users className="w-3 h-3 sm:w-4 sm:h-4" />{course.total_enrolled || 0}</span>
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3 sm:w-4 sm:h-4" />{course.total_modules || 0} modules</span>
                              <span className="font-medium text-indigo-600">{course.is_free ? 'Free' : `₹${course.price_inr?.toLocaleString()}`}</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2">
                            {(course.status === 'draft' || course.status === 'archived') && <Button size="sm" variant="outline" onClick={() => handlePublishCourse(course.id)} title="Publish"><Send className="w-4 h-4" /></Button>}
                            {course.status === 'published' && <Button size="sm" variant="outline" onClick={() => handleArchiveCourse(course.id)} title="Archive"><Archive className="w-4 h-4" /></Button>}
                            <Button size="sm" variant="outline" onClick={() => navigate(`/courses/${course.slug || course.id}`)} title="View"><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" onClick={() => openEditCourse(course)} title="Edit"><Pencil className="w-4 h-4" /></Button>
                            {/* Delete button only visible for full admin, not sub_admin */}
                            {user.lms_role === 'admin' && (
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteCourse(course.id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                            )}
                            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate(`/lms-admin/courses/${course.id}`)}>Manage<ChevronRight className="w-4 h-4 ml-1" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Courses Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(pagination.total_pages)].map((_, i) => (
                      <Button
                        key={i}
                        variant={pagination.page === i + 1 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i + 1)}
                        className={pagination.page === i + 1 ? "bg-indigo-600" : ""}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                  >
                    Next
                  </Button>
                  
                  <span className="text-sm text-gray-500 ml-2">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total_items} courses)
                  </span>
                </div>
              )}
            </TabsContent>
            
            {/* Batches Tab */}
            <TabsContent value="batches">
              {batches.length === 0 ? (
                <Card className="text-center py-8 sm:py-12">
                  <CardContent>
                    <Layers className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">No batches yet</h3>
                    <p className="text-gray-500 text-sm mb-4">Create a batch for your courses</p>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowBatchModal(true)} disabled={courses.length === 0}><Plus className="w-4 h-4 mr-2" />Create Batch</Button>
                    {courses.length === 0 && <p className="text-xs text-gray-400 mt-2">Create a course first</p>}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {batches.map((batch) => (
                    <Card key={batch.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-base sm:text-lg font-bold text-gray-900">{batch.name}</h3>
                              {getStatusBadge(batch.status)}
                            </div>
                            <p className="text-gray-600 text-sm mb-2">{batch.course_title}</p>
                            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3 sm:w-4 sm:h-4" />{new Date(batch.start_date).toLocaleDateString()}</span>
                              <span className="flex items-center gap-1"><Users className="w-3 h-3 sm:w-4 sm:h-4" />{batch.current_enrolled || 0}/{batch.max_students}</span>
                              <span className="flex items-center gap-1"><Video className="w-3 h-3 sm:w-4 sm:h-4" />{batch.total_sessions} sessions</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2">
                            {batch.status === 'draft' && <Button size="sm" className="bg-blue-700 hover:bg-blue-700" onClick={() => handleOpenBatch(batch.id)}>Open</Button>}
                            {batch.status === 'open' && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleStartBatch(batch.id)}>Start</Button>}
                            {batch.status === 'in_progress' && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleCompleteBatch(batch.id)}>Complete</Button>}
                            <Button size="sm" variant="outline" onClick={() => navigate(`/lms-admin/batches/${batch.id}`)}>Manage<ChevronRight className="w-4 h-4 ml-1" /></Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Batches Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(pagination.total_pages)].map((_, i) => (
                      <Button
                        key={i}
                        variant={pagination.page === i + 1 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i + 1)}
                        className={pagination.page === i + 1 ? "bg-indigo-600" : ""}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                  >
                    Next
                  </Button>
                  
                  <span className="text-sm text-gray-500 ml-2">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total_items} batches)
                  </span>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Course Modal */}
      <Dialog open={showCourseModal} onOpenChange={setShowCourseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</DialogTitle>
            <DialogDescription>Fill in the course information</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Course Title *</Label><Input value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} placeholder="e.g., Introduction to Machine Learning" /></div>
            <div className="space-y-2"><Label>Short Description *</Label><SimpleRichTextEditor value={courseForm.short_description} onChange={(val) => setCourseForm({ ...courseForm, short_description: val })} placeholder="Brief description of the course" compact={true} /></div>
            
            {/* Thumbnail Upload */}
            <div className="space-y-2">
              <Label>Course Thumbnail/Cover</Label>
              <p className="text-xs text-gray-500 mb-2">Upload an image or video to use as course thumbnail (Max 10MB)</p>
              
              {courseForm.thumbnail_url ? (
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4">
                  {courseForm.thumbnail_url.startsWith('data:video/') ? (
                    <video src={courseForm.thumbnail_url} className="w-full h-48 object-cover rounded" controls />
                  ) : (
                    <img src={courseForm.thumbnail_url} alt="Course thumbnail" className="w-full h-48 object-cover rounded" loading="lazy" decoding="async"/>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={removeThumbnail}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*,video/mp4,video/webm"
                    onChange={handleThumbnailUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed border-2"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploadingThumbnail}
                  >
                    {uploadingThumbnail ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Video className="w-4 h-4 mr-2" />
                        Upload Thumbnail (Image/Video)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Category Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Categories
              </Label>
              <p className="text-xs text-gray-500 mb-2">Select one or more categories for this course</p>
              
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50/50">
                {categories.map(category => (
                  <Badge
                    key={category.name}
                    onClick={() => toggleCategorySelection(category.name)}
                    className={`cursor-pointer transition-all px-3 py-1.5 ${
                      courseForm.categories.includes(category.name)
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                    }`}
                  >
                    {category.name}
                    {courseForm.categories.includes(category.name) && (
                      <X className="w-3 h-3 ml-1" />
                    )}
                  </Badge>
                ))}
                
                {/* Add New Category */}
                {showNewCategoryInput ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      className="w-40 h-8 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                    />
                    <Button size="sm" onClick={handleCreateCategory} className="h-8 bg-indigo-600">
                      Add
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategoryName('');
                    }} className="h-8">
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Badge
                    onClick={() => setShowNewCategoryInput(true)}
                    className="cursor-pointer bg-white hover:bg-gray-100 text-indigo-600 border border-dashed border-indigo-400"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Category
                  </Badge>
                )}
              </div>
              
              {courseForm.categories.length > 0 && (
                <p className="text-xs text-gray-500">
                  Selected: {courseForm.categories.join(', ')}
                </p>
              )}
            </div>
            
            <div className="space-y-2"><Label>Full Description</Label><SimpleRichTextEditor value={courseForm.full_description} onChange={(val) => setCourseForm({ ...courseForm, full_description: val })} placeholder="Detailed description with course highlights, curriculum overview, outcomes..." minHeight="120px" /></div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Duration (weeks)</Label><Input type="number" min={1} value={courseForm.duration_weeks} onChange={(e) => setCourseForm({ ...courseForm, duration_weeks: parseInt(e.target.value) })} /></div>
              <div className="space-y-2"><Label>Mode</Label><Select value={courseForm.mode} onValueChange={(v) => setCourseForm({ ...courseForm, mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="live_web">Live Web</SelectItem><SelectItem value="self_paced">Self-Paced</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent></Select></div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Difficulty</Label><Select value={courseForm.difficulty_level} onValueChange={(v) => setCourseForm({ ...courseForm, difficulty_level: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Language</Label><Input value={courseForm.language} onChange={(e) => setCourseForm({ ...courseForm, language: e.target.value })} /></div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"><div><Label>Free Course</Label><p className="text-xs text-gray-500">Students can enroll without payment</p></div><Switch checked={courseForm.is_free} onCheckedChange={(v) => setCourseForm({ ...courseForm, is_free: v, price_inr: v ? 0 : courseForm.price_inr })} /></div>
            {!courseForm.is_free && <div className="space-y-2"><Label>Price (INR)</Label><Input type="number" min={0} value={courseForm.price_inr} onChange={(e) => setCourseForm({ ...courseForm, price_inr: parseInt(e.target.value) })} /></div>}
            
            {/* Instructor Details */}
            <div className="space-y-4 p-4 border border-blue-200 rounded-lg bg-blue-50/30">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <GraduationCap className="w-4 h-4" /> Instructor Details
              </h4>
              <div className="space-y-2">
                <Label>Instructor Name</Label>
                <Input 
                  value={courseForm.instructor_name} 
                  onChange={(e) => setCourseForm({ ...courseForm, instructor_name: e.target.value })} 
                  placeholder="Enter instructor name"
                />
              </div>
              <div className="space-y-2">
                <Label>Instructor Photo</Label>
                <div className="flex items-start gap-4">
                  {courseForm.instructor_image && (
                    <img 
                      src={courseForm.instructor_image} 
                      alt="Instructor" 
                      className="w-24 h-24 object-cover border border-gray-200" loading="lazy" decoding="async"/>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error('Image must be less than 2MB');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setCourseForm({ ...courseForm, instructor_image: reader.result });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Recommended: Square image, max 2MB</p>
                    {courseForm.instructor_image && (
                      <button 
                        type="button"
                        onClick={() => setCourseForm({ ...courseForm, instructor_image: '' })}
                        className="text-xs text-red-600 hover:text-red-800 mt-1"
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Instructor Bio</Label>
                <textarea 
                  value={courseForm.instructor_bio} 
                  onChange={(e) => setCourseForm({ ...courseForm, instructor_bio: e.target.value })} 
                  placeholder="Brief description of the instructor's background and expertise"
                  className="w-full min-h-[80px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"><div><Label>Certificate</Label><p className="text-xs text-gray-500">Issue certificates to students</p></div><Switch checked={courseForm.certificate_enabled} onCheckedChange={(v) => setCourseForm({ ...courseForm, certificate_enabled: v })} /></div>
            
            {/* Certificate Customization - Only show when certificate is enabled */}
            {courseForm.certificate_enabled && (
              <div className="space-y-4 p-4 border border-indigo-200 rounded-lg bg-indigo-50/30">
                <h4 className="font-medium text-indigo-900 flex items-center gap-2">
                  <Award className="w-4 h-4" /> Certificate Customization
                </h4>
                
                {/* Template Selection */}
                <div className="space-y-2">
                  <Label>Certificate Template</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CERTIFICATE_TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setCourseForm({ ...courseForm, certificate_template: template.id })}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          courseForm.certificate_template === template.id 
                            ? 'border-indigo-500 bg-white shadow-md' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className={`h-2 rounded mb-2 bg-gradient-to-r ${template.gradient}`} />
                        <p className="text-xs font-medium text-gray-700">{template.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Signatory Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Signatory Name</Label>
                    <Input 
                      value={courseForm.certificate_signatory_name} 
                      onChange={(e) => setCourseForm({ ...courseForm, certificate_signatory_name: e.target.value })} 
                      placeholder="e.g., Dr. John Smith"
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Signatory Title</Label>
                    <Input 
                      value={courseForm.certificate_signatory_title} 
                      onChange={(e) => setCourseForm({ ...courseForm, certificate_signatory_title: e.target.value })} 
                      placeholder="e.g., Course Director"
                      className="text-sm"
                    />
                  </div>
                </div>
                
                {/* Custom Text */}
                <div className="space-y-1">
                  <Label className="text-xs">Custom Achievement Text (Optional)</Label>
                  <Input 
                    value={courseForm.certificate_custom_text} 
                    onChange={(e) => setCourseForm({ ...courseForm, certificate_custom_text: e.target.value })} 
                    placeholder="e.g., with distinction in practical assessments"
                    className="text-sm"
                  />
                  <p className="text-xs text-gray-400">This text will appear below the course name</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"><div><Label>Recordings</Label><p className="text-xs text-gray-500">Allow recording access</p></div><Switch checked={courseForm.recordings_enabled} onCheckedChange={(v) => setCourseForm({ ...courseForm, recordings_enabled: v })} /></div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCourseModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={editingCourse ? handleUpdateCourse : handleCreateCourse} disabled={!courseForm.title || !courseForm.short_description}>{editingCourse ? 'Update' : 'Create'} Course</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Batch Modal */}
      <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Batch</DialogTitle>
            <DialogDescription>Set up a new batch for enrollment</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Select Course *</Label><Select value={batchForm.course_id} onValueChange={(v) => setBatchForm({ ...batchForm, course_id: v })}><SelectTrigger><SelectValue placeholder="Select a course" /></SelectTrigger><SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Batch Name *</Label><Input value={batchForm.name} onChange={(e) => setBatchForm({ ...batchForm, name: e.target.value })} placeholder="e.g., January 2025 Batch" /></div>
            <div className="space-y-2"><Label>Description</Label><SimpleRichTextEditor value={batchForm.description} onChange={(val) => setBatchForm({ ...batchForm, description: val })} placeholder="Batch details, schedule highlights, special notes..." compact={true} /></div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date *</Label><Input type="date" value={batchForm.start_date} onChange={(e) => setBatchForm({ ...batchForm, start_date: e.target.value })} /></div>
              <div className="space-y-2"><Label>End Date *</Label><Input type="date" value={batchForm.end_date} onChange={(e) => setBatchForm({ ...batchForm, end_date: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Max Students</Label><Input type="number" min={1} value={batchForm.max_students} onChange={(e) => setBatchForm({ ...batchForm, max_students: parseInt(e.target.value) })} /></div>
            
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              <Label className="text-base font-semibold">Schedule</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Class Time</Label><Input type="time" value={batchForm.schedule_pattern.class_time} onChange={(e) => setBatchForm({ ...batchForm, schedule_pattern: { ...batchForm.schedule_pattern, class_time: e.target.value } })} /></div>
                <div className="space-y-2"><Label>Duration (min)</Label><Input type="number" min={15} value={batchForm.schedule_pattern.class_duration_minutes} onChange={(e) => setBatchForm({ ...batchForm, schedule_pattern: { ...batchForm.schedule_pattern, class_duration_minutes: parseInt(e.target.value) } })} /></div>
              </div>
              <div className="space-y-2"><Label>Days</Label>
                <div className="flex flex-wrap gap-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                    <Button key={day} type="button" variant={batchForm.schedule_pattern.weekly_days.includes(i) ? 'default' : 'outline'} size="sm" className={batchForm.schedule_pattern.weekly_days.includes(i) ? 'bg-indigo-600' : ''} onClick={() => {
                      const days = batchForm.schedule_pattern.weekly_days;
                      const newDays = days.includes(i) ? days.filter(d => d !== i) : [...days, i].sort();
                      setBatchForm({ ...batchForm, schedule_pattern: { ...batchForm.schedule_pattern, weekly_days: newDays } });
                    }}>{day}</Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateBatch} disabled={!batchForm.course_id || !batchForm.name || !batchForm.start_date || !batchForm.end_date || batchForm.schedule_pattern.weekly_days.length === 0}>Create Batch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default LMSAdminDashboard;
